import { startRegistration } from "@simplewebauthn/browser"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  changePhone,
  deletePushSubscription,
  getProfile,
  getWebauthnRegisterOptions,
  registerPushSubscription,
  registerWebauthnCredential,
  updateProfile,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatDateTime, maskPhone } from "../utils/formatting"
import { useAuth } from "../stores/auth"

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const normalized = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = atob(normalized)
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0))
}

export function ProfilePage() {
  const { t } = useTranslation()
  const { client, logout } = useAuth()
  const profileQuery = useQuery({
    queryKey: ["portal-profile"],
    queryFn: () => getProfile().then((response) => response.data),
  })
  const [altPhone, setAltPhone] = useState("")
  const [physicalAddress, setPhysicalAddress] = useState("")
  const [preferredLanguage, setPreferredLanguage] = useState<"en" | "sn">("en")
  const [pushEnabled, setPushEnabled] = useState(false)
  const [securityFeedback, setSecurityFeedback] = useState("")
  const [newPhoneNumber, setNewPhoneNumber] = useState("+263")
  const [phoneOtp, setPhoneOtp] = useState("")
  const [changeRequestId, setChangeRequestId] = useState<string | null>(null)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    if (profileQuery.data) {
      setAltPhone(profileQuery.data.altPhone ?? "")
      setPhysicalAddress(profileQuery.data.physicalAddress ?? "")
      setPreferredLanguage((profileQuery.data.preferredLanguage as "en" | "sn") ?? "en")
    }
  }, [profileQuery.data])

  useEffect(() => {
    let active = true
    if (!("serviceWorker" in navigator)) {
      return
    }

    void navigator.serviceWorker.ready.then(async (registration) => {
      const subscription = await registration.pushManager.getSubscription()
      if (active) {
        setPushEnabled(Boolean(subscription))
      }
    })

    return () => {
      active = false
    }
  }, [])

  const updateMutation = useMutation({
    mutationFn: () =>
      updateProfile({
        altPhone: altPhone || null,
        physicalAddress: physicalAddress || null,
        preferredLanguage,
      }),
    onSuccess: () => profileQuery.refetch(),
  })

  const pushMutation = useMutation({
    mutationFn: async () => {
      if (!("serviceWorker" in navigator) || !profileQuery.data?.webPushPublicKey) {
        throw new Error("Push notifications are not available on this device.")
      }

      const registration = await navigator.serviceWorker.ready
      const existing = await registration.pushManager.getSubscription()

      if (existing) {
        await deletePushSubscription(existing.endpoint)
        await existing.unsubscribe()
        return false
      }

      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        throw new Error("Notification permission was not granted.")
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(profileQuery.data.webPushPublicKey),
      })

      const json = subscription.toJSON()
      await registerPushSubscription({
        endpoint: json.endpoint!,
        keys: {
          p256dh: json.keys?.p256dh ?? "",
          auth: json.keys?.auth ?? "",
        },
        contentEncoding: "aes128gcm",
      })
      return true
    },
    onSuccess: (enabled) => {
      setPushEnabled(Boolean(enabled))
      setSecurityFeedback(
        enabled ? t("profile.enableNotifications") : t("profile.disableNotifications"),
      )
    },
    onError: (error) => {
      setSecurityFeedback((error as Error).message)
    },
  })

  const biometricMutation = useMutation({
    mutationFn: async () => {
      const response = await getWebauthnRegisterOptions()
      const registration = await startRegistration(response.data.options as any)
      await registerWebauthnCredential({
        challengeId: response.data.challengeId,
        response: registration as unknown as Record<string, unknown>,
      })
    },
    onSuccess: () => {
      if (client) {
        window.localStorage.setItem(
          "portal_biometric_client",
          JSON.stringify({
            clientId: client.clientId,
            fullName: client.fullName,
            phoneNumber: client.phoneNumber,
          }),
        )
      }
      setSecurityFeedback(t("profile.enableBiometric"))
    },
    onError: (error) => {
      setSecurityFeedback((error as Error).message)
    },
  })

  const phoneChangeMutation = useMutation({
    mutationFn: async () => {
      if (!changeRequestId) {
        return changePhone({ step: "request_otp", newPhoneNumber })
      }

      return changePhone({ step: "verify_otp", changeRequestId, otp: phoneOtp })
    },
    onSuccess: async (response) => {
      const data = response.data as Record<string, unknown>
      if (typeof data.changeRequestId === "string") {
        setChangeRequestId(data.changeRequestId)
        setSecurityFeedback(t("profile.phoneOtp"))
        return
      }

      setSecurityFeedback(t("login.changePhone"))
      await logout()
    },
    onError: (error) => {
      setSecurityFeedback((error as Error).message)
    },
  })

  if (profileQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={180} />
        <Skeleton height={220} />
      </div>
    )
  }

  const profile = profileQuery.data
  if (!profile) return null

  const imageDocument = profile.documents.find((document) => document.mimeType.startsWith("image/"))

  return (
    <div className="page-stack">
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t("profile.title")}</p>
            <h3>{profile.fullName}</h3>
          </div>
        </div>
        <dl className="metric-grid">
          <div>
            <dt>{t("profile.primaryPhone")}</dt>
            <dd>{revealed ? profile.phoneNumber : maskPhone(profile.phoneNumber)}</dd>
          </div>
          <div>
            <dt>{t("profile.kycStatus")}</dt>
            <dd>{profile.kycStatus}</dd>
          </div>
        </dl>
        <button className="ghost-button" onClick={() => setRevealed((value) => !value)} type="button">
          {revealed ? "Hide full phone number" : "Reveal full phone number"}
        </button>
        {profileQuery.dataUpdatedAt ? (
          <div className="last-updated">
            Last updated {formatDateTime(new Date(profileQuery.dataUpdatedAt).toISOString())}
          </div>
        ) : null}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t("profile.title")}</p>
            <h3>{t("profile.updateTitle")}</h3>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault()
            updateMutation.mutate()
          }}
        >
          <label>
            {t("profile.altPhone")}
            <input onChange={(event) => setAltPhone(event.target.value)} value={altPhone} />
          </label>
          <label>
            {t("profile.address")}
            <textarea
              onChange={(event) => setPhysicalAddress(event.target.value)}
              rows={4}
              value={physicalAddress}
            />
          </label>
          <label>
            {t("profile.language")}
            <select
              onChange={(event) => setPreferredLanguage(event.target.value as "en" | "sn")}
              value={preferredLanguage}
            >
              <option value="en">English</option>
              <option value="sn">chiShona</option>
            </select>
          </label>
          <button className="primary-button" disabled={updateMutation.isPending} type="submit">
            {updateMutation.isPending ? t("profile.saving") : t("profile.save")}
          </button>
        </form>
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t("notifications.title")}</p>
            <h3>{t("profile.notifications")}</h3>
          </div>
        </div>
        <div className="button-row">
          <button
            className="ghost-button"
            onClick={() => pushMutation.mutate()}
            type="button"
          >
            {pushEnabled ? t("profile.disableNotifications") : t("profile.enableNotifications")}
          </button>
          <button
            className="ghost-button"
            disabled={biometricMutation.isPending}
            onClick={() => biometricMutation.mutate()}
            type="button"
          >
            {t("profile.enableBiometric")}
          </button>
        </div>
        {securityFeedback ? <div className="status-banner success">{securityFeedback}</div> : null}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t("profile.changePhone")}</p>
            <h3>{t("profile.changePhone")}</h3>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault()
            phoneChangeMutation.mutate()
          }}
        >
          {!changeRequestId ? (
            <label>
              {t("profile.newPhone")}
              <input
                onChange={(event) => setNewPhoneNumber(event.target.value)}
                type="tel"
                value={newPhoneNumber}
              />
            </label>
          ) : (
            <label>
              {t("profile.phoneOtp")}
              <input
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setPhoneOtp(event.target.value)}
                value={phoneOtp}
              />
            </label>
          )}
          <button className="primary-button" disabled={phoneChangeMutation.isPending} type="submit">
            {changeRequestId ? t("profile.verifyPhone") : t("profile.changePhone")}
          </button>
        </form>
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Documents</p>
            <h3>National ID record</h3>
          </div>
        </div>
        {imageDocument ? (
          <img
            alt={imageDocument.fileName}
            className="document-preview"
            height={180}
            loading="lazy"
            src={imageDocument.downloadPath}
            width={320}
          />
        ) : (
          <p className="empty-copy">No image document is available in this environment.</p>
        )}
      </section>
    </div>
  )
}
